import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Third Party Libraries
import moment from 'moment';

// Hooks
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  createEnvelopeProof,
  createEnvelopeProofOrder,
  createViewProofForOrder,
} from '~/utils/template-builder';
import { failure, success } from '~/redux/actions/snackbar-actions';
import { cancelOrder, payAndProcess } from '~/redux/actions/orders-actions';
import { addBusinessDays } from '~/components/Orders/CreateOrderNew/OrderFlow/OrderDetails';
import { getUserData } from '~/redux/actions/auth-actions.js';
import useSortParams from '~/hooks/useSortParams';

//Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { balanceFormatter, numberWithComma, stringLength } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';
import { getNoResultsMessage, MESSAGES } from '~/utils/message';

// Components
import OlcActionMenu from '~/components/General/OlcActionMenu';
import ConfirmDialog from '~/components/Orders/ConfirmDialog/ConfirmDialog';
import AddFunds from '~/components/Account/Wallet/AddFunds';
import ConfirmModal from '~/components/General/ConfirmModal';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';

// MUI Table
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Box,
  MenuItem,
  Typography,
  Tooltip,
  CircularProgress,
  Menu,
  Checkbox,
  IconButton,
  Chip,
} from '@mui/material';

// Icons
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ScheduleOrderCIcon from '~/assets/images/create-orders/schedule-icon.svg';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MailIcon from '@mui/icons-material/MailOutlined';

// Icons
import {
  PaymentCIcon,
  CancelIconCIcon,
  CancelOrderCIcon,
  ProcessOrderNew,
  InsufficientCIcon,
  InfoWebhookCIcon,
  InfoCIcon,
  UncheckedCIcon,
  CheckedCIcon,
  RemovedCheckedCIcon,
  RescheduleActionCIcon,
  PayAndProcessActionCIcon,
  DownloadProofActionCIcon,
  CancelOrderNewCIcon,
  CancelOrderModalCIcon,
  ContactInfoCIcon,
} from '~/assets/images';

// Styles
import './styles.scss';

const getFullProductType = (product) =>
  [[product?.paperSize, product?.productType].filter(Boolean).join(' '), product?.deliveryType]
    .filter(Boolean)
    .join(', ') || ' -';

const OverflowTooltip = ({ value }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const displayValue = value || ' -';

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement) return undefined;

    const updateOverflow = () => {
      setIsOverflowing(textElement.scrollWidth > textElement.clientWidth + 1);
    };
    updateOverflow();

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateOverflow);
    resizeObserver?.observe(textElement);
    window.addEventListener('resize', updateOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverflow);
    };
  }, [displayValue]);

  return (
    <Tooltip disableHoverListener={!isOverflowing} title={displayValue}>
      <Typography ref={textRef}>{displayValue}</Typography>
    </Tooltip>
  );
};

const OrderTable = ({
  pagination,
  updatePagination,
  sort,
  onSortChange,
  setSelectedRows,
  selectedRows,
  searchApplied,
  setRescheduleModalOpen,
  setSelectedOrder,
  setActiveRow,
  getOrders,
  totalOrdersInDB,
  filterSize,
}) => {
  const [downloadingProof, setDownloadingProof] = useState({});
  const [downloadingEnvelopeProof, setDownloadingEnvelopeProof] = useState({});
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);
  const [showNoRows, setShowNoRows] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialog, setDialog] = useState({
    open: false,
    message: '',
    loading: false,
    currentAction: null,
    params: null,
  });
  const [fundsModal, setFundsModal] = useState({ open: false, params: null });
  const [activeRowId, setActiveRowId] = useState(null);
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    params: null,
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const pageSizes = DEFAULT_PAGE_SIZES;
  const open = Boolean(anchorEl);

  const fetchedRows = useSelector((state) => state.ordersReducers.orders);
  const rowsCount = useSelector((state) => state.ordersReducers.rowsCount);
  const to = useSelector((state) => state.ordersReducers.to);
  const from = useSelector((state) => state.ordersReducers.from);
  const lastPage = useSelector((state) => state.ordersReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.ordersReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.ordersReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const isLoading = useSelector((state) => state.ordersReducers.loading);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleCloseDialog = () => {
    setDialog({
      open: false,
      message: '',
      loading: false,
      params: null,
      currentAction: null,
      subMessage: '',
      heading: '',
      successButtonName: 'Yes',
    });
  };

  const cancelOrderHandler = async () => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    try {
      const payload = { orderId: activeRowId?.id };
      const response = await dispatch(cancelOrder(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        getOrders();
      } else {
        dispatch(failure(response.data.message));
      }
    } catch (error) {
      dispatch(failure(error.response.message || error.message));
    } finally {
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    }
  };

  const handleOk = async () => {
    setDialog((prev) => ({ ...prev, loading: true }));
    if (dialog.currentAction === 'payAndProcess') {
      const payload = { orderId: dialog.params.id };
      const response = await dispatch(payAndProcess(payload));
      if (response.status === 200) {
        dispatch(success(response.data.message));
        dispatch(getUserData());
        getOrders();
        setDialog((prev) => ({ ...prev, loading: false, open: false }));
      } else {
        if (
          response.data.message === 'Auto Funding Failed' ||
          response.data.message == 'Auto Funding is disabled'
        ) {
          setTimeout(() => {
            handleInsufficientFunds(dialog.params);
          }, 1000);
        } else {
          dispatch(failure(response.data.message));
        }
      }
      setDialog((prev) => ({ ...prev, loading: false, open: false }));
    } else if (dialog.currentAction == 'addFunds') {
      setFundsModal({ open: true, params: dialog.params });
    }
    setDialog((prev) => ({ ...prev, open: false }));
    setTimeout(() => {
      setDialog((prev) => ({ ...prev, loading: false }));
    }, 2000);
  };

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
  };

  const handleRowClick = (params, event) => {
    if (event.target.nodeName == 'IMG') {
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      window.open(`/orders/detail/overview/${params.id}`, '_blank');
    } else {
      navigate(`/orders/detail/overview/${params.id}`);
    }
  };

  const downloadViewProof = async (params) => {
    try {
      setDownloadingProof((prev) => ({
        ...prev,
        [params.id]: true,
      }));

      await dispatch(
        createViewProofForOrder(
          `Order-${params.id}-Mailer-Proof`,
          {
            orderId: params.id,
          },
          'order',
          activeRowId.templateType
        )
      );
      setDownloadingProof((prev) => ({
        ...prev,
        [params.id]: false,
      }));
      handleClose();
    } catch (error) {
      setDownloadingProof((prev) => ({
        ...prev,
        [params.id]: false,
      }));
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    }
  };

  const downloadEnvelopeProof = async (params) => {
    try {
      setDownloadingEnvelopeProof((prev) => ({
        ...prev,
        [params.id]: true,
      }));
      let payload = {
        orderId: params?.id,
      };
      await dispatch(createEnvelopeProof(`Order-${params.id}-Envelope-Proof`, payload));
      handleClose();
    } catch (error) {
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setDownloadingEnvelopeProof((prev) => ({
        ...prev,
        [params.id]: false,
      }));
    }
  };

  const handleCancelOrder = async (params) => {
    setOpenConfirmModal({
      type: 'cancel',
      open: true,
      header: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
      message: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.MESSAGE,
      params: params,
      loading: false,
      handleOk: cancelOrderHandler,
      handleClose: () => {
        setOpenConfirmModal((prev) => ({
          ...prev,
          open: false,
        }));
      },
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.SUB_MESSAGE,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.CANCEL.HEADING,
      icon: CancelOrderModalCIcon,
    });
  };

  const handlePayAndProcessOrder = async (params) => {
    const asapDate = addBusinessDays(new Date(), 2);
    asapDate.setHours(0, 0, 0, 0);

    let mailingDateDisplay;
    if (params.rawMailingDate) {
      const orderMailingDate = new Date(params.rawMailingDate);
      orderMailingDate.setHours(0, 0, 0, 0);

      if (orderMailingDate < asapDate) {
        mailingDateDisplay = dateFormat(asapDate, true);
      } else {
        mailingDateDisplay = params.mailingDate;
      }
    } else {
      mailingDateDisplay = dateFormat(asapDate, true);
    }

    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.MESSAGE,
      loading: false,
      currentAction: 'payAndProcess',
      params: params,
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUCCESS_BUTTON,
      subMessage: `${MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.SUB_MESSAGE}${mailingDateDisplay}`,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.PAY_AND_PROCESS.HEADING,
      icon: ProcessOrderNew,
    });
  };

  const handleInsufficientFunds = (params) => {
    setDialog({
      open: true,
      message: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.MESSAGE,
      loading: false,
      currentAction: 'addFunds',
      params: params,
      successButtonName: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUCCESS_BUTTON,
      subMessage: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.SUB_MESSAGE,
      heading: MESSAGES.ORDERS.DETAIL.MODALS.ADD_FUNDS.HEADING,
      icon: InsufficientCIcon,
    });
  };

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: contact.isFindNewContactsPurchaseOnly ? false : true,
    }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: contact.isFindNewContactsPurchaseOnly ? false : true,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows
      .filter((contact) => !contact.isFindNewContactsPurchaseOnly)
      .map((contact) => contact.id);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();
    if (selectedRow.isFindNewContactsPurchaseOnly) return;

    // Update the selectedRow object directly in the rows array
    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, checked: !row.checked } : row
    );

    // If the user selects all contacts and then removes a single contact, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.id === selectedRow.id
          ? { ...row, checked: !row.checked }
          : { ...row, checked: !row.isFindNewContactsPurchaseOnly }
      );

      const filteredRowsIds = filteredRows
        .map((contact) => contact.checked === true && contact.id)
        .filter(Boolean);

      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.id)
      ? selectedRows.filter((id) => id !== selectedRow.id) // Remove the ID if already selected
      : [...selectedRows, selectedRow.id]; // Add the ID if not selected

    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);

    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: contact.isFindNewContactsPurchaseOnly ? false : newBulkSelect,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows
      .filter((contact) => !contact.isFindNewContactsPurchaseOnly)
      .map((contact) => contact.id);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const [showActions, setShowActions] = React.useState(null);
  const openActions = Boolean(showActions);
  const handleClick = (event) => {
    event.stopPropagation();
    setShowActions(event.currentTarget);
  };
  const handleClose = () => {
    setShowActions(null);
  };

  const renderMenuItem = (activeRowId) => {
    if (activeRowId?.isFindNewContactsPurchaseOnly) return false;

    const itemsArray = [];
    const hold = activeRowId?.status == 'On Hold';
    const canceled = activeRowId?.status == 'Canceled';
    const scheduled = activeRowId?.status == 'Scheduled';
    const hasEnvelope = activeRowId?.product?.hasEnvelope;

    if (activeRowId?.paymentStatus !== 'Paid') {
      itemsArray.push(
        <MenuItem
          key={`reschedule-${activeRowId?.id}`}
          onClick={() => {
            setSelectedOrder(activeRowId.id);
            setActiveRow(activeRowId);
            setRescheduleModalOpen(true);
            handleClose();
          }}
        >
          <img src={RescheduleActionCIcon} alt="reschedule" />
          Reschedule
        </MenuItem>
      );
    }

    if (hold || canceled) {
      itemsArray.push(
        <MenuItem
          key={`pay-process-${activeRowId?.id}`}
          onClick={() => {
            handlePayAndProcessOrder(activeRowId);
            handleClose();
          }}
        >
          <img src={PayAndProcessActionCIcon} alt="pay&process" />
          Pay and Process
        </MenuItem>
      );
    }
    itemsArray.push(
      <MenuItem
        key={`download-proof-${activeRowId?.id}`}
        onClick={() => {
          if (!downloadingProof[activeRowId?.id]) {
            downloadViewProof(activeRowId);
          }
        }}
        disabled={downloadingProof[activeRowId?.id]}
      >
        {downloadingProof[activeRowId?.id] ? (
          <CircularProgress
            sx={{
              color: '#ed5c2f !important',
              width: '14px !important',
              height: '14px !important',
            }}
          />
        ) : (
          <img src={DownloadProofActionCIcon} alt="download proof" />
        )}
        {downloadingProof[activeRowId?.id] ? 'Downloading...' : 'Mailer Proof'}
      </MenuItem>
    );
    if (hasEnvelope) {
      itemsArray.push(
        <MenuItem
          onClick={() => downloadEnvelopeProof(activeRowId)}
          disabled={downloadingEnvelopeProof[activeRowId?.id]}
        >
          {downloadingEnvelopeProof[activeRowId?.id] ? (
            <CircularProgress
              sx={{
                color: '#ed5c2f !important',
                width: '14px !important',
                height: '14px !important',
              }}
            />
          ) : (
            <img src={DownloadProofActionCIcon} alt="download envelope" />
          )}
          {downloadingEnvelopeProof[activeRowId?.id] ? 'Downloading...' : 'Envelope Proof'}
        </MenuItem>
      );
    }
    if (activeRowId?.isCancellable && scheduled) {
      itemsArray.push(
        <MenuItem
          key={`cancel-order-${activeRowId?.id}`}
          onClick={() => {
            handleCancelOrder(activeRowId);
            handleClose();
          }}
        >
          <img src={CancelOrderNewCIcon} alt="Cancel" />
          Cancel
        </MenuItem>
      );
    }
    if (itemsArray.length) {
      return itemsArray;
    }
    return false;
  };

  const classes = {
    root: 'tableGrid',
    main: 'tableGridMain',
    columnHeaders: 'tableGridHeaderWrapper',
    columnHeader: 'tableGridColumnHeader',
    columnHeaderTitle: 'tableGridColumnHeaderTitle',
    virtualScrollerContent: 'tableGridVirtualScrollerContent',
    virtualScrollerRenderZone: 'tableGridVirtualScrollerRenderZone',
    cell: 'tableGridCell',
    row: 'tableGridRow',
    columnHeaderTitleContainer: 'tableGridColumnHeaderTitleContainer',
    virtualScroller: 'tableGridVirtualScroller',
    cellContent: 'tableGridCellContent',
    cellTextRight: 'tableGridCellTextRight',
    sortIcon: 'tableGridSortIcon',
    columnSeparator: 'tableGridColumnSeparator',
    footerContainer: 'tableGridFooterContainer',
    columnHeaderDraggableContainer: 'tableGridColumnHeaderDraggableContainer',
  };

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck"
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={RemovedCheckedCIcon} alt="removed checked" />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck"
              icon={<img src={UncheckedCIcon} alt="unchecked" />}
              checkedIcon={<img src={CheckedCIcon} alt="checked" />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open order selection options"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            classes={{
              paper: 'menuOptionsPaper',
              list: 'menuOptionsList',
            }}
            sx={{
              marginTop: '7px',
            }}
            key={activeRowId?.id}
          >
            <MenuItem
              onClick={rowsCount > 0 ? selectAll : undefined}
              className={selectedRows[0] === -1 ? 'highlight' : ''}
            >
              {MESSAGES.CONTACTS.SELECT_ALL_ITEMS} ({rowsCount})
            </MenuItem>
            <MenuItem onClick={selectVisble}>{MESSAGES.CONTACTS.SELECT_VISIBLE_ITEMS}</MenuItem>
            <MenuItem onClick={selectNone}>{MESSAGES.CONTACTS.SELECT_NONE}</MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck"
            icon={<img src={UncheckedCIcon} alt="unchecked" />}
            checkedIcon={<img src={CheckedCIcon} alt="checked" />}
            checked={params.row.checked}
            disabled={params.row.isFindNewContactsPurchaseOnly}
          />
        </Box>
      ),
    },
    {
      field: 'id',
      headerName: 'Order Name/ID',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 180,
      sortable: true,
      sortKey: 'name',
      renderCell: (params) => (
        <Box className="newStyles">
          <Tooltip
            title={`${params?.row?.name ? params?.row?.name : ' -'}`}
            disableHoverListener={params?.row?.name.length > 15 ? false : true}
          >
            <Typography>{params?.row?.name ? params?.row?.name : ' -'}</Typography>
            <Typography>
              ID: {params?.row?.id}
              {params.row.meta && 'automationStepIndex' in params.row.meta ? (
                <Chip label="Campaign" color="primary" className="campaignChip" />
              ) : null}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'productType',
      headerName: 'Product',
      headerClassName: 'productColumn',
      cellClassName: 'productColumn',
      width: 150,
      sortable: true,
      renderCell: (params) => (
        <Box
          className="addressWrapper productValueWrapper"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '5px',
            minWidth: 0,
            width: '100%',
          }}
        >
          <OverflowTooltip value={params.row.productType} />
        </Box>
      ),
    },
    {
      field: 'productDetails',
      headerName: 'Product Type',
      headerClassName: 'productTypeColumn',
      cellClassName: 'productTypeColumn',
      width: 280,
      sortable: true,
      flex: window.innerWidth >= 1650 ? 1.4 : 0,
      renderCell: (params) => {
        const fullProductType =
          params.row.findNewContactsPurchase?.listType || getFullProductType(params.row.product);

        return (
          <Box className="addressWrapper">
            <OverflowTooltip value={fullProductType} />
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Order Status',
      type: 'number',
      width: 130,
      sortable: true,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.status}</Typography>
        </Box>
      ),
    },
    {
      field: 'deliverableQuantity',
      headerName: 'Quantity',
      width: 100,
      sortable: true,
      sortKey: 'quantity',
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>
            {params.row.isFindNewContactsPurchaseOnly &&
            params.row.findNewContactsPurchase?.quantity !== undefined
              ? numberWithComma(params.row.findNewContactsPurchase.quantity)
              : params.row.deliverableQuantity}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'cost',
      headerName: 'Cost',
      width: 100,
      sortable: true,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{balanceFormatter(params.row.cost)}</Typography>
        </Box>
      ),
    },
    {
      field: 'mailingDate',
      renderHeader: () => (
        <Box className="scheduledDateHeader">
          <Typography className="headerName">Mailing Date</Typography>
          <Tooltip title="The date mailers will be sent to our mailhouse.">
            <img
              className="scheduledDateHeaderIcon"
              src={ContactInfoCIcon}
              alt="icon"
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
        </Box>
      ),
      width: 200,
      sortable: true,
      flex: window.innerWidth >= 1650 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.mailingDate}</Typography>
        </Box>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Payment Status',
      width: 200,
      sortable: true,
      flex: window.innerWidth >= 1650 ? 1 : 0,
      renderCell: (params) => (
        <Box className="addressWrapper">
          <Typography>{params.row.paymentStatus}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      width: 200,
      sortable: true,
      flex: window.innerWidth >= 1650 ? 0.7 : 0,
      renderCell: (params) =>
        stringLength(params?.row?.createdBy, 15) ? (
          <Tooltip title={`${params?.row?.createdBy}`}>
            <Box className="createdByWrapper">
              <Typography>{params?.row?.createdBy || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="createdByWrapper">
            <Typography>{params?.row?.createdBy || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 200,
      sortable: true,
      flex: window.innerWidth >= 1650 ? 0.7 : 0,
      renderCell: (params) =>
        stringLength(params?.row?.createdBy, 15) ? (
          <Tooltip title={`${params?.row?.createdAt}`}>
            <Box className="createdByWrapper">
              <Typography>{params?.row?.createdAt || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="createdByWrapper">
            <Typography>{params?.row?.createdAt || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box>
            <Tooltip title={renderMenuItem(params.row) ? null : 'No Actions Available'}>
              <IconButton
                aria-label={`Open actions for order ${params.row.name || params.row.id}`}
                id={`order-actions-${params.row.id}`}
                aria-controls={
                  openActions && activeRowId?.id === params.row.id ? 'long-menu' : undefined
                }
                aria-expanded={
                  openActions && activeRowId?.id === params.row.id ? 'true' : undefined
                }
                aria-haspopup="true"
                onClick={(event) => {
                  renderMenuItem(params.row) ? setActiveRowId(params.row) : undefined;
                  renderMenuItem(params.row) ? handleClick(event) : event.stopPropagation();
                }}
                disableRipple={renderMenuItem(params.row) ? false : true}
                sx={{
                  cursor: renderMenuItem(params.row) ? 'pointer' : 'not-allowed',
                  opacity: renderMenuItem(params.row) ? '1' : '0.5',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <MoreVertIcon
                  sx={{
                    opacity: activeRowId?.id === params.row.id && openActions ? '0.8' : '0.4',
                    '&:hover': {
                      opacity: '0.8',
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      ),
    },
  ];

  // Server-side sorting: the Orders page owns the sort (it drives the fetch); the grid restores it.
  const { sortProps } = useSortParams(columns, onSortChange, sort);

  // Page, page size and sort changes start a new result set: drop the selection and uncheck the rows
  // that are still on screen until the new page arrives.
  useEffect(() => {
    setSelectedRows([]);
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    setRows((currentRows) => currentRows.map((row) => ({ ...row, checked: false })));
  }, [currentPage, perPage, sort?.sortBy, sort?.sortOrder]);

  // Purchase-only (Find New Contacts) orders can't be selected, so "all selected" means every
  // selectable row on the page - comparing with rows.length would never match when they are present.
  useEffect(() => {
    const selectableRowsCount = rows.filter((row) => !row.isFindNewContactsPurchaseOnly).length;

    !selectedRows.length && setRemoveBulkSelect(false);
    (selectableRowsCount > 0 && selectedRows.length === selectableRowsCount) ||
    selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  // New data (search, filters, refresh after an action) replaces the rows with unchecked ones, so the
  // selection that referred to the old rows must go too - otherwise ids and checkboxes disagree.
  useLayoutEffect(() => {
    setRows(fetchedRows);
    setSelectedRows([]);
  }, [fetchedRows]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  // Add delay for showing the empty-state overlay.
  useEffect(() => {
    let timeoutId;

    if (!isLoading && rows.length === 0) {
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300);
    } else {
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, rows.length]);

  return (
    <div
      style={{
        width: '100%',
      }}
      className="ordersTable olcPrimaryListContent olcPrimaryListTable"
    >
      <DataGrid
        rows={rows}
        columns={columns}
        fillAvailableWidth
        shrinkColumnsToFit
        loading={isLoading}
        rowCount={rowsCount}
        pagination={pagination}
        paginationMode="server"
        {...sortProps}
        onRowClick={handleRowClick}
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        getRowId={(row) => row?.id}
        rowHeight={61}
        disableColumnMenu={true}
        classes={{
          ...classes,
          virtualScroller: 'tableGridVirtualScroller virtualScroller',
        }}
        columnHeaderHeight={47}
        emptyState={{
          visible: showNoRows,
          message: getNoResultsMessage({ searchApplied, filterSize }),
        }}
      />
      {(isLoading || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) =>
            updatePagination({
              page: 1,
              pageSize: event.target.value,
            })
          }
          from={from}
          to={to}
          total={rowsCount}
          loading={isLoading}
        />
      )}
      {dialog.open && (
        <ConfirmDialog
          dialog={dialog}
          open={dialog.open}
          loading={dialog.loading}
          handleClose={handleCloseDialog}
          handleOk={handleOk}
        />
      )}

      {openConfirmModal.open && (
        <ConfirmModal
          openConfirmModal={openConfirmModal}
          setOpenConfirmModal={setOpenConfirmModal}
        />
      )}

      {fundsModal.open && (
        <AddFunds
          params={fundsModal.params}
          open={fundsModal.open}
          getOrders={getOrders}
          handleOpen={() => {
            setFundsModal((prev) => ({ ...prev, open: true }));
          }}
          handleClose={() => {
            setFundsModal((prev) => ({ ...prev, open: false }));
          }}
          onSuccessCallback={async () => {
            const response = await dispatch(payAndProcess({ orderId: fundsModal.params.id }));
            if (response.status === 200) {
              dispatch(getUserData());
              dispatch(success(response.data.message));
              // refreshOverview && refreshOverview();
              getOrders && getOrders();
            } else {
              dispatch(failure(response.data.message));
            }
          }}
        />
      )}
      <OlcActionMenu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': activeRowId ? `order-actions-${activeRowId.id}` : undefined,
        }}
        anchorEl={showActions}
        open={openActions}
        onClose={handleClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        {renderMenuItem(activeRowId)}
      </OlcActionMenu>
    </div>
  );
};

export default OrderTable;
